import React from "react"

import { useTranslation } from "react-i18next"

import {Modal} from "components/modals/Modal"

import { PromoCodeBonus, PromoCodeUsage } from "services/bonus/type"

import { RedemptionCard } from "./RedemptionCard"

export interface PromoCodeUsageStats {
  bonus: PromoCodeBonus
  usages: PromoCodeUsage[]
  /** Absent when bonus-service returns no stats block. */
  stats?: {
    totalRedemptions: number
    totalBonusAwarded: number
    totalWagered: number
  }
}

interface Props {
  usageData: PromoCodeUsageStats
  onClose: () => void
}

export const PromoCodeUsageModal = ({ usageData, onClose }: Props) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const { bonus, usages, stats } = usageData

  return (
    <Modal open={true} handler={onClose}>
      <div className="bg-[#1A1A2E] rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-white">
            {t("bonus2.promo.usage")}: {bonus.promo_code}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
            ×
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-[#0C0C1A] rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple">{stats?.totalRedemptions ?? 0}</div>
            <div className="text-sm text-gray-400">{t("bonus2.promo.totalRedemptions")}</div>
          </div>
          <div className="bg-[#0C0C1A] rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">{bonus.activeCount}</div>
            <div className="text-sm text-gray-400">{t("bonus2.promo.active")}</div>
          </div>
          <div className="bg-[#0C0C1A] rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{bonus.completedCount}</div>
            <div className="text-sm text-gray-400">{t("bonus2.promo.completed")}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[#0C0C1A] rounded-lg p-4 text-center">
            <div className="text-xl font-bold text-yellow-400">${((stats?.totalBonusAwarded ?? 0) / 100).toFixed(2)}</div>
            <div className="text-sm text-gray-400">{t("bonus2.promo.totalBonusAwarded")}</div>
          </div>
          <div className="bg-[#0C0C1A] rounded-lg p-4 text-center">
            <div className="text-xl font-bold text-orange-400">${((stats?.totalWagered ?? 0) / 100).toFixed(2)}</div>
            <div className="text-sm text-gray-400">{t("bonus2.promo.totalWagered")}</div>
          </div>
        </div>

        {/* Redemptions List */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-300">{t("bonus2.promo.recentRedemptions", { count: usages.length })}</h4>
          {usages.length === 0 ? (
            <div className="text-gray-500 text-center py-4">{t("bonus2.promo.noRedemptionsYet")}</div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {usages.map((u) => (
                <RedemptionCard key={u.id} redemption={u} />
              ))}
            </div>
          )}
        </div>

        <button onClick={onClose} className="mt-6 w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
          {t("bonus2.promo.close")}
        </button>
      </div>
    </Modal>
  )
}
